import { useEffect, type RefObject } from 'react';

/**
 * Keyboard shortcuts for a list (`plan-beta` E).
 *
 * ### The rule that makes shortcuts safe to add
 *
 * **A shortcut never fires while someone is typing.** `/` focusing the search box
 * is useful; `/` deleting itself into a prompt an author is halfway through
 * writing is a bug report. So every handler here bails on an editable target,
 * and that check lives in one place rather than in each binding — the version
 * where one binding forgets is the version that loses somebody's work.
 *
 * Modifier combinations are also left alone: `Ctrl+/`, `Cmd+K` and the browser's
 * own bindings belong to the browser and the OS, not to this list.
 */
function isTyping(target: EventTarget | null): boolean {
  const el = target as HTMLElement | null;
  if (!el) return false;
  const tag = el.tagName;
  return (
    tag === 'INPUT'
    || tag === 'TEXTAREA'
    || tag === 'SELECT'
    || el.isContentEditable
    // A Radix combobox is a button, not an input, and typing into it filters.
    || el.getAttribute('role') === 'combobox'
  );
}

/**
 * Every search box `/` could mean, so that it can only mean one of them.
 *
 * The topbar search (X2) is mounted on **every** screen, and three list screens
 * bind `/` to their own box. Two window listeners both calling `.focus()` is a
 * race decided by mount order — which changes between a first paint and a
 * client-side navigation, so the same keystroke would land in different boxes
 * depending on how the operator arrived.
 *
 * The tie is therefore broken by intent rather than by timing: a screen's own
 * box always wins, and the global one is a `fallback` that answers only when no
 * screen has claimed the key.
 */
interface SearchTarget {
  ref: RefObject<HTMLInputElement | null>;
  fallback: boolean;
}

const SEARCH_TARGETS = new Set<SearchTarget>();

/**
 * `/` focuses the list's search box.
 *
 * The one shortcut with no visible affordance, which is why the search input
 * carries it in its placeholder.
 *
 * Pass `{ fallback: true }` for a box that should only be focused when the
 * current screen has no search of its own — the topbar, and nothing else.
 */
export function useSearchShortcut(
  ref: RefObject<HTMLInputElement | null>,
  options: { fallback?: boolean } = {},
): void {
  const fallback = options.fallback ?? false;

  useEffect(() => {
    const target: SearchTarget = { ref, fallback };
    SEARCH_TARGETS.add(target);

    const onKey = (event: KeyboardEvent) => {
      if (event.key !== '/' || event.metaKey || event.ctrlKey || event.altKey) return;
      if (isTyping(event.target)) return;

      // Resolved at press time, not at mount time: the set is what is on screen
      // NOW, and a screen's own box outranks the global one whichever mounted
      // first.
      const targets = [...SEARCH_TARGETS];
      const winner = targets.find((t) => !t.fallback) ?? targets[0];
      if (winner !== target) return;

      event.preventDefault();
      ref.current?.focus();
      ref.current?.select();
    };

    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
      SEARCH_TARGETS.delete(target);
    };
  }, [ref, fallback]);
}

/**
 * `j` / `k` move between rows, `Enter` opens the focused one.
 *
 * Implemented by moving DOM focus rather than by tracking an index in state.
 * Focus is the thing a screen reader announces and the thing `Enter` already
 * acts on, so a "focused row" that only React knew about would be a second,
 * invisible cursor that disagrees with the real one the moment anyone uses Tab.
 *
 * `j`/`k` are vi's, and the walkthrough asks for them; the arrow keys are left
 * to the browser because inside a scrolling table they already mean scroll.
 */
export function useRowShortcuts(container: RefObject<HTMLElement | null>): void {
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      if (event.key !== 'j' && event.key !== 'k') return;
      if (isTyping(event.target)) return;

      const rows = [...(container.current?.querySelectorAll<HTMLElement>('[data-row-focus]') ?? [])];
      if (rows.length === 0) return;

      event.preventDefault();
      const current = rows.findIndex((row) => row === document.activeElement || row.contains(document.activeElement));
      const next = event.key === 'j'
        ? Math.min(rows.length - 1, current + 1)
        // From nothing focused, `k` starts at the last row rather than jumping
        // to the first — the same asymmetry every list navigation has.
        : current <= 0 ? rows.length - 1 : current - 1;

      rows[next]?.focus();
      rows[next]?.scrollIntoView({ block: 'nearest' });
    };

    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [container]);
}
